import React from 'react'
import { Box, Button } from '@mui/material'
// eslint-disable-next-line no-restricted-imports
import { useLocation } from 'react-router'
import { INode } from 'shared/types/tree'
import { fetchTreesAndIdMaps, nodeForItem } from './utility'
import { NodeDndToc, WithParentId } from '../dnd_toc/types'
import { createIdMap, NodeIdMap } from '../version_mapping/utility'
import { DndCurriculaTree } from '../dnd_toc/DndCurriculaTree'
import { TreeEditingContext } from './context'
import { useCopySourceNodeBeforeDestNode } from './treeNodeEditing'
import { goToTree } from '../tree/TreeActions'
import { ConfirmDialog } from 'shared/ui'
import { TreeTextField } from '../tree/navigationHelpers/TreeTextField'
import { NavItem } from '../table_of_contents/types'
import { PotentiallyHtml } from '../PotentiallyHtml'
import { NodeView } from './NodeView'
import { useNavigateWithSearch } from 'infrastructure/routing'

const treesParam = 'trees'

const createTreesById = (trees: INode[]) =>
  trees.reduce<{ [k: string]: INode }>((acc, tree) => {
    acc[`${tree.tree_id}:${tree.version}`] = tree
    return acc
  }, {})
export function TreeEditing() {
  const [showTreeInput, setShowTreeInput] = React.useState(false)
  const [treesById, setTreesById] = React.useState<{ [k: string]: INode }>({})
  const [treeIds, setTreeIDs] = React.useState<string[]>([])
  const [idMaps, setIdMaps] = React.useState<{ [k: string]: NodeIdMap }>({})
  const [nodeDropped, setNodeDropped] = React.useState<{
    droppedItem: WithParentId<NodeDndToc>
    nextItem: WithParentId<NodeDndToc>
  }>()
  const [message, setMessage] = React.useState('')

  const { pathname, search } = useLocation()
  const navigate = useNavigateWithSearch()

  const getTrees = React.useCallback((treeIds: string[]) => {
    setMessage('Loading trees....')
    fetchTreesAndIdMaps(...treeIds).then(({ trees, idMaps }) => {
      setTreesById((prev) => ({ ...prev, ...createTreesById(trees) }))
      setIdMaps((prev) => ({ ...prev, ...idMaps }))
      setMessage('')
    })
  }, [])

  React.useEffect(() => {
    const treeIds = (searchParams(search)[treesParam]?.split(',') ?? []).map(
      (ids) => {
        const [tid, vid] = ids.split(':')
        return `${tid}:${vid}`
      },
    )
    setTreeIDs(treeIds)
  }, [search])

  React.useEffect(() => {
    const filtered = treeIds.filter((tid) => !(tid in treesById))
    if (!filtered.length) return
    getTrees(filtered)
  }, [treeIds, getTrees, treesById])

  const contextValue = React.useMemo(() => {
    return {
      idMaps,
      trees: treesById,
      treesUpdated: (result: string[] | INode) => {
        if (Array.isArray(result)) {
          return getTrees(result)
        }
        const treeIds = `${result.tree_id}:${result.version}`
        setTreesById((prev) => ({ ...prev, [treeIds]: result }))
        setIdMaps((prev) => ({ ...prev, [treeIds]: createIdMap(result) }))
      },
      onItemDrop: async (
        droppedItem: WithParentId<NodeDndToc>,
        nextItem: WithParentId<NodeDndToc>,
      ) => {
        setNodeDropped({ droppedItem, nextItem })
      },
      removeTree: (treeId: string) => {
        const treeIds = (
          searchParams(search)[treesParam]?.split(',') ?? []
        ).filter((tid) => !tid.includes(treeId))
        navigate({
          pathname,
          search: treeIds.length ? `?${treesParam}=${treeIds.join(',')}` : '',
        })
      },
    }
  }, [idMaps, getTrees, treesById, history, pathname, search])

  const onTreeInput = ({
    treeId,
    version,
  }: {
    treeId: string
    version: string
    nodeId?: string
  }) => {
    setShowTreeInput(false)
    const treeIds = searchParams(search)[treesParam]?.split(',') ?? []
    treeIds.push(`${treeId}:${version}`)
    navigate({
      pathname,
      search: `?${treesParam}=${treeIds.join(',')}`,
    })
  }
  const onDone = React.useCallback(() => setNodeDropped(undefined), [])
  return (
    <Box>
      <TreeEditingContext.Provider value={contextValue}>
        <Box>
          Tree Editing{' '}
          <Button onClick={() => setShowTreeInput(!showTreeInput)}>
            Add Tree
          </Button>
        </Box>
        {message && (
          <Box padding={'5px'}>
            <PotentiallyHtml s={message} />
          </Box>
        )}
        {showTreeInput && <TreeTextField onChange={onTreeInput} />}
        <ConfirmOperation
          droppedItem={nodeDropped?.droppedItem}
          nextItem={nodeDropped?.nextItem}
          onDone={onDone}
        />
        <Box display={'flex'} padding={'10px'}>
          {treeIds.map((treeId) => (
            <Box key={treeId} paddingRight="25px">
              <TreeView treeId={treeId} />
            </Box>
          ))}
        </Box>
      </TreeEditingContext.Provider>
    </Box>
  )
}

function searchParams(search?: string) {
  const params: { [k: string]: string } = {}
  return (search?.split('?')[1]?.split('&') || []).reduce((acc, param) => {
    const [key, val] = param.split('=')
    acc[key] = val
    return acc
  }, params)
}

function ConfirmOperation({
  onDone = () => {},
  droppedItem,
  nextItem,
}: {
  onDone: () => void
  droppedItem?: WithParentId<NodeDndToc>
  nextItem?: WithParentId<NodeDndToc>
}) {
  const doCopy = useCopySourceNodeBeforeDestNode()
  const [operation, setOperation] = React.useState<() => unknown>()
  const [title, setTitle] = React.useState('')
  const [message, setMessage] = React.useState('')
  const { idMaps, treesUpdated } = React.useContext(TreeEditingContext)

  const setValues = React.useCallback(
    ({
      operation,
      title = '',
      message = '',
    }: {
      operation?: () => unknown
      title?: string
      message?: string
    }) => {
      setOperation(operation)
      setTitle(title)
      setMessage(message)
    },
    [],
  )

  React.useEffect(() => {
    setValues({})
    if (!nextItem || !droppedItem) return
    const destIdString = `${nextItem.treeId}:${nextItem.version}`
    const sourceIdString = `${droppedItem.treeId}:${droppedItem.version}`
    const dropNode = nodeForItem(droppedItem, idMaps)
    const nextNode = nodeForItem(nextItem, idMaps)
    const { message, operation, title } = doCopy({
      source: dropNode,
      dest: nextNode,
      sourceIdMap: idMaps[sourceIdString],
      destIdMap: idMaps[destIdString],
    })
    const wrappedOperation = operation
      ? async () => {
          const treeIds = await operation()
          onDone()
          treesUpdated(treeIds)
        }
      : undefined
    setValues({
      title,
      message,
      operation: () => wrappedOperation,
    })
  }, [droppedItem, nextItem, idMaps, setValues, onDone, treesUpdated])

  if (!operation) return null

  return (
    <>
      {message && (
        <Box padding={'15px'}>
          <PotentiallyHtml s={message} />
        </Box>
      )}
      <ConfirmDialog title={title} onConfirm={operation} onClose={onDone}>
        <PotentiallyHtml s={message} />
      </ConfirmDialog>
    </>
  )
}

function TreeView({ treeId }: { treeId: string }) {
  const [selectedItem, setSelectedItem] = React.useState<NavItem>()
  const { onItemDrop, trees, removeTree } = React.useContext(TreeEditingContext)
  const tree = trees[treeId]
  const go = () => {
    goToTree({
      treeId: tree.tree_id,
      versionId: tree.version,
    })
  }
  const remove = () => {
    removeTree(treeId)
  }
  if (!tree) return null
  return (
    <Box>
      <Box>
        <strong>{tree.title}</strong>
        <Box>
          <Button onClick={remove}>X</Button>
          <Button onClick={go}>Go</Button>
        </Box>
      </Box>
      <Box maxHeight={'420px'} overflow={'auto'}>
        <DndCurriculaTree
          tree={tree}
          onItemDrop={onItemDrop}
          selectedItem={selectedItem?.id}
          onItemSelect={setSelectedItem}
        />
      </Box>
      {selectedItem && (
        <Box padding={'10px'}>
          <NodeView nodeId={selectedItem.id} treeId={treeId} />
        </Box>
      )}
    </Box>
  )
}
